import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Image, Card, Button, Form, Badge } from 'react-bootstrap';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { listProductDetails } from '../actions/productActions';
import { addToCartWithOptions } from '../actions/cartActions';
import {
    BOOK_PRODUCT_ID,
    LANGUAGE_ADDON_PRODUCT_ID,
    PRINT_PRODUCT_ID,
} from '../constants/productConfig';
import { usePricing } from '../hooks/usePricing';
import { useTranslation } from 'react-i18next';
import { BOOK_LANGUAGE_VALUES, getNativeLanguageName } from '../constants/languageNames';

function ProductScreen() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { bookCreationWithCopyPrice, additionalCopyPrice } = usePricing();

    const cartItems = useSelector(state => state.cart.cartItems);

    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [printCopies, setPrintCopies] = useState(1);
    const [adding, setAdding] = useState(false);

    const productDetails = useSelector(state => state.productDetails);
    const { loading, error, product } = productDetails;
    const hasBaseBookInCart = cartItems.some((item) => (
        Number(item.product) === BOOK_PRODUCT_ID || item?.metadata?.lineItemType === 'book_base'
    ));
    const printProductImage = '/site-assets/book-cover-flat.jpg';

    useEffect(() => {
        dispatch(listProductDetails(id));
    }, [dispatch, id]);

    const isConfigurator = Number(id) === BOOK_PRODUCT_ID;
    const configuratorImage = '/site-assets/book-cover-flat.jpg';
    const configuratorName = t('product.configuratorName');
    const configuratorDescription = t('product.configuratorDescription');
    const configuratorBadges = [
        t('home.proofRhyme'),
        t('home.proofReference'),
        t('home.proofLanguages'),
    ];

    const basePrice = bookCreationWithCopyPrice; // Creation + 1 copy included
    const additionalCopiesPrice = printCopies > 1 ? (printCopies - 1) * additionalCopyPrice : 0;
    const addonPrice = 0; // Language addon is now free
    const totalPrice = basePrice + addonPrice + additionalCopiesPrice;

    const handleAddToCart = async () => {
        if (!selectedLanguage) {
            alert(t('product.selectLanguageAlert'));
            return;
        }

        setAdding(true);
        try {
            await dispatch(addToCartWithOptions(
                BOOK_PRODUCT_ID,
                {
                    customPrice: basePrice,
                    customName: t('product.lineItemBookCreationOneCopy'),
                    qty: 1,
                    metadata: {
                        lineItemType: 'book_base',
                        includedCopies: 1,
                    },
                }
            ));
            if (selectedLanguage !== 'English') {
                await dispatch(addToCartWithOptions(
                    LANGUAGE_ADDON_PRODUCT_ID,
                    {
                        customPrice: 0,
                        customName: t('product.lineItemNonEnglishBook', {
                            language: getNativeLanguageName(selectedLanguage, selectedLanguage)
                        }),
                        qty: 1,
                        metadata: {
                            lineItemType: 'language_addon',
                            language: selectedLanguage,
                        },
                    }
                ));
            }
            if (printCopies > 1) {
                const extraCopies = printCopies - 1;
                const extraCopiesPrice = extraCopies * additionalCopyPrice;
                await dispatch(addToCartWithOptions(
                    PRINT_PRODUCT_ID,
                    {
                        customPrice: extraCopiesPrice,
                        customName: t(
                            extraCopies === 1
                                ? 'product.lineItemAdditionalCopy'
                                : 'product.lineItemAdditionalCopies',
                            { count: extraCopies }
                        ),
                        qty: 1,
                        extraCopies,
                        metadata: {
                            lineItemType: 'additional_copies',
                            extraCopies,
                        },
                    }
                ));
            }
            navigate('/cart');
        } finally {
            setAdding(false);
        }
    };

    // Fallback for non-configurator products
    if (!isConfigurator) {
        return (
            <div>
                <Link className='btn btn-light my-3' to='/products'>{t('product.goBack')}</Link>
                {loading ? <Loader /> : error ? <Message variant='danger'>{error}</Message> : (
                    <Row>
                        <Col md={6}>
                            <Image src={Number(id) === PRINT_PRODUCT_ID ? printProductImage : product.image} alt={product.name} fluid />
                        </Col>
                        <Col md={6}>
                            <h3>{product.name}</h3>
                            <p>{product.description}</p>
                            <strong>{t('product.amountDKK', { amount: product.price })}</strong>
                            <Button
                                className='d-block mt-3'
                                onClick={() => navigate(`/cart/${id}?qty=1`)}
                                disabled={!product.count_in_stock}
                            >
                                {t('product.addToCartSimple')}
                            </Button>
                        </Col>
                    </Row>
                )}
            </div>
        );
    }

    return (
        <div>
            <Link className='btn btn-light my-3' to='/products'>{t('product.goBack')}</Link>

            {loading ? <Loader /> : error ? <Message variant='danger'>{error}</Message> : (
                <>
                    {/* Hero */}
                    <Row className='mb-5 align-items-center'>
                        <Col md={5}>
                            <Image src={configuratorImage} alt={configuratorName} fluid rounded className='shadow' />
                        </Col>
                        <Col md={7}>
                            <h1>{configuratorName}</h1>
                            <p className='lead text-muted'>{configuratorDescription}</p>
                            <div className='d-flex flex-wrap gap-2 mt-3'>
                                {configuratorBadges.map((badge) => (
                                    <Badge key={badge} bg='light' text='dark' pill className='border'>
                                        {badge}
                                    </Badge>
                                ))}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        {/* Options */}
                        <Col md={8}>
                            <Card className='mb-3'>
                                <Card.Header><strong>{t('product.customiseOrder')}</strong></Card.Header>
                                <Card.Body className='p-0'>

                                    {/* Base book + 1 printed copy */}
                                    <Row className='align-items-center p-3 border-bottom m-0'>
                                        <Col>
                                            <strong>{t('product.bookCreation')}</strong>
                                            <div><small className='text-muted'>{t('product.bookCreationDesc')}</small></div>
                                        </Col>
                                        <Col xs='auto'><Badge bg='secondary' className='fs-6'>{t('product.amountDKK', { amount: basePrice })}</Badge></Col>
                                    </Row>

                                    {/* Language add-on */}
                                    <Row className='align-items-start p-3 border-bottom m-0'>
                                        <Col>
                                            <strong>{t('product.bookLanguage')}</strong>
                                            <div><small className='text-muted'>{t('product.nonEnglishDesc')}</small></div>
                                            <Form.Select
                                                className='mt-2'
                                                style={{ maxWidth: '240px' }}
                                                value={selectedLanguage}
                                                onChange={e => setSelectedLanguage(e.target.value)}
                                            >
                                                <option value="">{t('product.selectLanguage')}</option>
                                                <option value="English">{getNativeLanguageName('English', 'English')}</option>
                                                {BOOK_LANGUAGE_VALUES.filter(lang => lang !== 'English').map(lang => (
                                                    <option key={lang} value={lang}>{getNativeLanguageName(lang, lang)}</option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                        <Col xs='auto'>
                                            <Badge bg='success' className='fs-6'>{t('product.included')}</Badge>
                                        </Col>
                                    </Row>

                                    {/* Additional printed copies */}
                                    <Row className='align-items-start p-3 m-0'>
                                        <Col>
                                            <strong>{t('product.additionalCopies')}</strong>
                                            <div><small className='text-muted'>{t('product.additionalCopiesDesc')}</small></div>
                                            <div className='mt-2 d-flex align-items-center gap-2'>
                                                <Form.Control
                                                    type='number'
                                                    min={1}
                                                    max={20}
                                                    value={printCopies}
                                                    onChange={(e) => setPrintCopies(Math.max(1, parseInt(e.target.value) || 1))}
                                                    style={{ width: '80px' }}
                                                />
                                                <small className='text-muted'>
                                                    {printCopies === 1 ? t('product.copy') : t('product.copies')}
                                                </small>
                                            </div>
                                        </Col>
                                        <Col xs='auto'>
                                            {printCopies > 1 ? (
                                                <Badge bg='secondary' className='fs-6'>{t('product.amountDKK', { amount: additionalCopiesPrice })}</Badge>
                                            ) : (
                                                <Badge bg='light' text='dark' className='fs-6'>{t('product.amountDKK', { amount: 0 })}</Badge>
                                            )}
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Summary */}
                        <Col md={4}>
                            <Card className='sticky-top' style={{ top: '80px' }}>
                                <Card.Header><strong>{t('product.orderSummary')}</strong></Card.Header>
                                <Card.Body>
                                    <div className='d-flex justify-content-between mb-2'>
                                        <span>{t('product.bookCreation')}</span>
                                        <span>{t('product.amountDKK', { amount: basePrice })}</span>
                                    </div>
                                    {selectedLanguage && selectedLanguage !== 'English' && (
                                        <div className='d-flex justify-content-between mb-2'>
                                            <span>{t('product.nonEnglishLang', { language: getNativeLanguageName(selectedLanguage, selectedLanguage) })}</span>
                                            <span>{t('product.amountDKK', { amount: 0 })}</span>
                                        </div>
                                    )}
                                    {additionalCopiesPrice > 0 && (
                                        <div className='d-flex justify-content-between mb-2'>
                                            <span>{t('product.printCount', { count: printCopies - 1, label: (printCopies - 1) === 1 ? t('product.copy') : t('product.copies') })}</span>
                                            <span>{t('product.amountDKK', { amount: additionalCopiesPrice })}</span>
                                        </div>
                                    )}
                                    <hr />
                                    <div className='d-flex justify-content-between fw-bold fs-5 mb-1'>
                                        <span>{t('product.total')}</span>
                                        <span>{t('product.amountDKK', { amount: totalPrice })}</span>
                                    </div>
                                    {printCopies > 1 && (
                                        <small className='text-muted'>{t('product.shippingNote')}</small>
                                    )}
                                    <Button
                                        className='w-100 mt-3'
                                        variant='primary'
                                        size='lg'
                                        onClick={handleAddToCart}
                                        disabled={adding || hasBaseBookInCart}
                                    >
                                        {adding ? t('product.addingToCart') : t('product.addToCart')}
                                    </Button>
                                    {hasBaseBookInCart && (
                                        <small className='text-muted d-block mt-2'>
                                            {t('product.bookAlreadyInCart')}
                                        </small>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
}

export default ProductScreen;